/**
 * Matching store — UI state для /process/matching.
 *
 * Block M1a-FE (TZ 2026-07-29) + M1b property stage (TZ 2026-07-29):
 * composition (0..1 base template + 0..N manual properties) + library
 * apply/save + run wiring + property multivalue overrides cache.
 *
 * Persist (zustand `persist`, localStorage):
 *   - composition config: templateId, language, manualProperties,
 *     selectedInputColumns, multivalueDelimiter, tierKey, isTestRun,
 *     testRunLimit.
 *   - М1b: propertyMultivalueOverrides (per-property_key flag, §13.2)
 *     and propertyDelimiter (output delimiter, §13.4).
 *   - НЕ persist: pipelineId / confirmToken (ephemeral — invalidated on run).
 *
 * SPEC refs:
 *   - §5.3 language: marketplace template → UA|RU picker; user template →
 *     fixed display.
 *   - §9.2 composition: 0..1 base + 0..N manual, NOT three exclusive screens.
 *   - §9.7 manual-only: templateId===null + ≥1 manual property.
 *   - §13.2 / §13.4 multivalue is configurable per determined property.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type MatchingLanguage = 'ua' | 'ru';

export type ManualPropertyDraft = {
  /** local uuid v4-ish (crypto.randomUUID на момент создания) */
  id: string;
  name: string;
  unit: string | null;
  /**
   * null = extract from evidence (§9.5).
   * Otherwise allowlist of allowed values.
   */
  allowedValues: string[] | null;
  /**
   * matching-v2 §D.4: how the property is filled.
   *   'only_mine' — value ONLY from allowedValues (enum), else free extract.
   *   'by_rule'  — deterministic formatter rule (format_rule on BE).
   */
  mode: 'only_mine' | 'by_rule';
  /** §D.4 by_rule rule text; only used when mode === 'by_rule'. */
  formatRule: string | null;
};

export type MultivalueDelimiter = ',' | ';' | '|' | '\n';

interface MatchingState {
  // === Composition ===
  /** null = без шаблона (manual-only mode, §9.7). */
  templateId: string | null;
  /**
   * Scope of the currently selected template. Tracked so the FE knows whether
   * the language picker should be editable (marketplace / global) or fixed
   * (user) per §5.3. Null = no template (manual-only mode, hide language picker).
   */
  templateScope: 'global' | 'user' | null;
  language: MatchingLanguage;
  manualProperties: ManualPropertyDraft[];
  selectedInputColumns: string[];
  /** §C: display order of dataset input columns (drag&drop reorder). */
  inputColumnOrder: string[];
  multivalueDelimiter: MultivalueDelimiter;

  // === Run ===
  tierKey: 'economy' | 'optimal' | 'max';
  isTestRun: boolean;
  /** Default 5 (TZ M1a §6 — тестовый прогон). */
  testRunLimit: number;
  /** Ephemeral. */
  pipelineId: string | null;
  /** Ephemeral. */
  confirmToken: string | null;

  // === M1b: per-property multivalue overrides (§13.2 / §13.4) ===
  propertyMultivalueOverrides: Record<string, boolean>;
  propertyDelimiter: MultivalueDelimiter;

  // === Setters ===
  setTemplateId: (id: string | null) => void;
  /**
   * Atomically set template id + scope (avoids two-step state desync).
   * Pass `null` for both when clearing (manual-only mode).
   */
  setTemplate: (
    id: string | null,
    scope: 'global' | 'user' | null,
  ) => void;
  setLanguage: (lang: MatchingLanguage) => void;
  addManualProperty: () => void;
  updateManualProperty: (
    id: string,
    patch: Partial<Omit<ManualPropertyDraft, 'id'>>,
  ) => void;
  removeManualProperty: (id: string) => void;
  setManualProperties: (next: ManualPropertyDraft[]) => void;
  toggleInputColumn: (col: string) => void;
  setSelectedInputColumns: (cols: string[]) => void;
  /** §C: full reorder of the input-column chips (drag&drop). */
  setInputColumnOrder: (cols: string[]) => void;
  /** §C: move `key` before `beforeKey` (HTML5 dnd drop target). */
  reorderInputColumn: (key: string, beforeKey: string) => void;
  setMultivalueDelimiter: (d: MultivalueDelimiter) => void;

  setTierKey: (t: 'economy' | 'optimal' | 'max') => void;
  setIsTestRun: (v: boolean) => void;
  setTestRunLimit: (n: number) => void;

  setPipelineId: (id: string | null) => void;
  setConfirmToken: (token: string | null) => void;

  // === M1b: multivalue overrides ===
  setPropertyMultivalue: (key: string, on: boolean) => void;
  setAllPropertyMultivalue: (next: Record<string, boolean>) => void;
  setPropertyDelimiter: (d: MultivalueDelimiter) => void;

  reset: () => void;
}

const DEFAULTS = {
  templateId: null as string | null,
  templateScope: null as 'global' | 'user' | null,
  language: 'ua' as MatchingLanguage,
  manualProperties: [] as ManualPropertyDraft[],
  selectedInputColumns: [] as string[],
  inputColumnOrder: [] as string[],
  multivalueDelimiter: ',' as MultivalueDelimiter,

  tierKey: 'optimal' as const,
  isTestRun: true,
  testRunLimit: 5,

  pipelineId: null as string | null,
  confirmToken: null as string | null,

  propertyMultivalueOverrides: {} as Record<string, boolean>,
  propertyDelimiter: ',' as MultivalueDelimiter,
};

/** Мин-валидация для manual property name — non-empty, не длиннее 64. */
function sanitizeName(raw: string): string {
  return raw.trim().slice(0, 64);
}

export const useMatchingStore = create<MatchingState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setTemplateId: (id) =>
        set({ templateId: id, pipelineId: null, confirmToken: null }),
      /**
       * Set template id + scope atomically. Used by MethodBlock where the scope
       * is known at pick time (global/user query lists). When scope is null,
       * also clears templateScope (manual-only mode).
       */
      setTemplate: (id, scope) =>
        set({
          templateId: id,
          templateScope: scope,
          pipelineId: null,
          confirmToken: null,
        }),
      setLanguage: (lang) =>
        set({ language: lang, pipelineId: null, confirmToken: null }),

      addManualProperty: () =>
        set((state) => {
          // Лимит 8 карточек чтобы не превращать экран в свалку. §9.3 «3-4 grid».
          if (state.manualProperties.length >= 8) return state;
          const newId =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : `mp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          return {
            manualProperties: [
              ...state.manualProperties,
              {
                id: newId,
                name: '',
                unit: null,
                allowedValues: null,
                mode: 'only_mine',
                formatRule: null,
              },
            ],
          };
        }),

      updateManualProperty: (id, patch) =>
        set((state) => ({
          manualProperties: state.manualProperties.map((p) =>
            p.id === id
              ? {
                  ...p,
                  ...patch,
                  name: patch.name !== undefined ? sanitizeName(patch.name) : p.name,
                }
              : p,
          ),
        })),

      removeManualProperty: (id) =>
        set((state) => ({
          manualProperties: state.manualProperties.filter((p) => p.id !== id),
        })),

      setManualProperties: (next) =>
        set({
          manualProperties: next.map((p) => ({ ...p, name: sanitizeName(p.name) })),
        }),

      toggleInputColumn: (col) =>
        set((state) => ({
          selectedInputColumns: state.selectedInputColumns.includes(col)
            ? state.selectedInputColumns.filter((c) => c !== col)
            : [...state.selectedInputColumns, col],
        })),

      setSelectedInputColumns: (cols) =>
        set({ selectedInputColumns: [...cols] }),

      setInputColumnOrder: (cols) => set({ inputColumnOrder: [...cols] }),

      reorderInputColumn: (key, beforeKey) =>
        set((state) => {
          const order = state.inputColumnOrder.filter((c) => c !== key);
          const at = order.indexOf(beforeKey);
          if (at === -1) return state;
          order.splice(at, 0, key);
          return { inputColumnOrder: order };
        }),

      setMultivalueDelimiter: (d) => set({ multivalueDelimiter: d }),

      setTierKey: (t) => set({ tierKey: t }),
      setIsTestRun: (v) => set({ isTestRun: v }),
      // Clamp 1..20 — aligns with backend PipelineStartRequest.test_run_limit
  // (ge=1, le=20). Previously 50, which let the UI offer values the backend
  // rejects with 422. Fix 2026-08-02: M1a matching /pipeline/start 422.
  setTestRunLimit: (n) => set({ testRunLimit: Math.max(1, Math.min(20, n)) }),

      setPipelineId: (id) => set({ pipelineId: id }),
      setConfirmToken: (token) =>
        set({ confirmToken: token, pipelineId: token ? null : null }),

      setPropertyMultivalue: (key, on) =>
        set((state) => ({
          propertyMultivalueOverrides: {
            ...state.propertyMultivalueOverrides,
            [key]: on,
          },
        })),
      setAllPropertyMultivalue: (next) =>
        set({ propertyMultivalueOverrides: { ...next } }),
      setPropertyDelimiter: (d) => set({ propertyDelimiter: d }),

      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: 'cs_matching_config',
      partialize: (state) => ({
        templateId: state.templateId,
        templateScope: state.templateScope,
        language: state.language,
        manualProperties: state.manualProperties,
        selectedInputColumns: state.selectedInputColumns,
        inputColumnOrder: state.inputColumnOrder,
        multivalueDelimiter: state.multivalueDelimiter,
        tierKey: state.tierKey,
        isTestRun: state.isTestRun,
        testRunLimit: state.testRunLimit,
        propertyMultivalueOverrides: state.propertyMultivalueOverrides,
        propertyDelimiter: state.propertyDelimiter,
      }),
    },
  ),
);
